import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileSpreadsheet, Clock, Layers, Sparkles, Link2, ArrowRight, BarChart3, Database, Code2, Lock, Users, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAssessmentLibrary, assessmentTakeUrl, apiError, pickRegionLink } from '../services/assessmentApi';
import { useRegion, getRegionLabel } from '../context/RegionContext';

const SOON = [
  { name: 'Power BI Dashboards', icon: BarChart3, text: 'DAX, data modelling and visual storytelling.' },
  { name: 'SQL for Analysts', icon: Database, text: 'Joins, aggregations and window functions on live data.' },
  { name: 'Python for Data', icon: Code2, text: 'pandas wrangling and analysis in a live notebook.' },
];

const Stat = ({ label, value }) => (
  <div>
    <p className="text-lg font-extrabold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? '—'}</p>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
  </div>
);

const TestCard = ({ test, onOpen }) => {
  const { region: selected } = useRegion();
  const [region, link] = pickRegionLink(test.public_links, selected);
  const copy = async () => {
    await navigator.clipboard.writeText(assessmentTakeUrl(link.token)).catch(() => {});
    toast.success(`Public test link (${getRegionLabel(region)}) copied`);
  };
  return (
    <div className="group relative rounded-2xl border bg-card p-6 flex flex-col gap-5 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200"
      data-testid={`assessment-card-${test.id}`}>
      <div className="flex items-start justify-between">
        <span className="w-12 h-12 rounded-xl bg-[#107C41] text-white flex items-center justify-center shadow-md shadow-emerald-900/20"><FileSpreadsheet className="h-6 w-6" /></span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-2 py-1 rounded-full">Live</span>
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{test.category}</p>
        <h3 className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{test.title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{test.description}</p>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {[[Clock, `${test.duration_minutes} min`], [Layers, `${test.min_skills}–${test.max_skills} skills`], [FileSpreadsheet, 'Live spreadsheet'], [Sparkles, 'AI feedback']].map(([I, t]) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-foreground/80"><I className="h-3 w-3" /> {t}</span>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 border-t pt-4">
        <Stat label="Attempts" value={test.stats.attempts} />
        <Stat label="Completed" value={test.stats.completed} />
        <Stat label="Avg score" value={test.stats.avg_score != null ? `${test.stats.avg_score}%` : null} />
        <Stat label="Invites" value={test.stats.invites} />
      </div>
      <div className="flex gap-2 mt-auto">
        <Button variant="outline" className="flex-1 gap-2" onClick={copy} disabled={!link.enabled} data-testid={`copy-public-test-link-btn-${test.id}`}>
          <Link2 className="h-4 w-4" /> Copy {getRegionLabel(region)} link
        </Button>
        <Button className="flex-1 gap-2" onClick={onOpen} data-testid={`open-assessment-btn-${test.id}`}>Manage & results <ArrowRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export default function AssessmentLibraryPage() {
  const navigate = useNavigate();
  const [tests, setTests] = useState(null);
  useEffect(() => {
    getAssessmentLibrary().then((d) => setTests(d.tests)).catch((e) => { toast.error(apiError(e)); setTests([]); });
  }, []);
  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-testid="assessment-library-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Assessment Library</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Skill tests that prove it.</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
            Send a link, the candidate picks the skills they claim, solves real tasks — you get a scored, AI-explained, shareable report.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Shared with every HireFast user</span>
      </div>
      {!tests ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {tests.map((t) => <TestCard key={t.id} test={t} onOpen={() => navigate(`/assessments/${t.id}`)} />)}
          {SOON.map(({ name, icon: I, text }) => (
            <div key={name} className="rounded-2xl border border-dashed bg-muted/30 p-6 flex flex-col gap-3 opacity-80" data-testid={`assessment-coming-soon-${name.split(' ')[0].toLowerCase()}`}>
              <span className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center"><I className="h-6 w-6 text-muted-foreground" /></span>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</h3>
              <p className="text-sm text-muted-foreground">{text}</p>
              <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Coming soon</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
